import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ModalPlantillaRipsRapida from "./ModalPlantillaRipsRapida";

const MODAL_CSS = `
.cups-rips-modal-portal .chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
.cups-rips-modal-portal .chip-filter {
  border: 1px solid rgba(23,162,184,0.25);
  background: rgba(23,162,184,0.06);
  color: #17a2b8;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
}
.cups-rips-modal-portal .chip-filter.active {
  background: #17a2b8;
  color: #fff;
  border-color: #17a2b8;
}
.cups-rips-modal-portal .cups-rips-nombre-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
}
.cups-rips-modal-portal .lista-precios-nombre-wrap {
  position: relative;
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  cursor: default;
}
.cups-rips-modal-portal .lista-precios-nombre {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cups-rips-modal-portal .lista-precios-nombre-wrap[data-tip]:hover::after {
  content: attr(data-tip);
  position: absolute;
  left: 0;
  bottom: calc(100% + 8px);
  z-index: 40;
  max-width: min(420px, 70vw);
  padding: 8px 12px;
  background: #2c3e50;
  color: #fff;
  font-size: 12px;
  line-height: 1.35;
  white-space: normal;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  pointer-events: none;
}
.cups-rips-modal-portal .lista-precios-nombre-wrap[data-tip]:hover::before {
  content: '';
  position: absolute;
  left: 14px;
  bottom: calc(100% + 2px);
  z-index: 41;
  border: 6px solid transparent;
  border-top-color: #2c3e50;
  pointer-events: none;
}
.cups-rips-modal-portal .cups-rips-plantilla-check {
  flex: 0 0 auto;
  color: #28a745;
  font-size: 0.95rem;
}
.cups-rips-modal-portal .cups-rips-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.cups-rips-modal-portal .btn-crear-plantilla {
  border: 1px solid rgba(50, 58, 69, 0.22);
  background: rgba(50, 58, 69, 0.06);
  color: #2f3945;
  font-weight: 600;
}
.cups-rips-modal-portal .btn-crear-plantilla:hover {
  background: rgba(50, 58, 69, 0.12);
  color: #202830;
}
`;

function tienePlantilla(item, tipo, extras = {}) {
    if (!item) return false;
    const extraId = extras[String(item.id)];
    if (extraId) return true;
    if (tipo === "procedimiento") {
        return !!item.plantilla_procedimiento_id;
    }
    return !!item.plantilla_consulta_id;
}

/**
 * Modal de selección CUPS (consulta/procedimiento) estilo CIE10.
 */
function ModalCupsServicioRips({
    show,
    onClose,
    onSelect,
    title = "Seleccionar Código CUPS",
    tipo = "consulta",
    items = [],
    onPlantillaCreada,
}) {
    const [filtro, setFiltro] = useState("");
    const [clasificacion, setClasificacion] = useState("");
    const [servicioPlantilla, setServicioPlantilla] = useState(null);
    const [plantillasLocales, setPlantillasLocales] = useState({});
    const searchRef = useRef(null);

    useEffect(() => {
        if (!show) return undefined;
        setFiltro("");
        setClasificacion("");
        setServicioPlantilla(null);
        const t = setTimeout(() => searchRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, [show]);

    const clasificaciones = useMemo(
        () =>
            Array.from(
                new Set(items.map((item) => String(item.clasificacion || "").trim()).filter(Boolean))
            ).sort((a, b) => a.localeCompare(b)),
        [items]
    );

    const filtrados = useMemo(() => {
        let list = items;
        if (clasificacion) {
            list = list.filter((item) => String(item.clasificacion ?? "").trim() === clasificacion);
        }
        const t = filtro.trim().toLowerCase();
        if (!t) return list;
        return list.filter((item) => {
            const cod = String(item.codigorips ?? "").toLowerCase();
            const nom = String(item.nombre ?? "").toLowerCase();
            return cod.includes(t) || nom.includes(t);
        });
    }, [items, filtro, clasificacion]);

    const abrirCrearPlantilla = (item) => {
        if (!item?.id) return;
        setServicioPlantilla(item);
    };

    const onPlantillaGuardada = (servicio, plantillaId) => {
        setPlantillasLocales((prev) => ({
            ...prev,
            [String(servicio.id)]: plantillaId || true,
        }));
        setServicioPlantilla(null);
        if (typeof onPlantillaCreada === "function") {
            onPlantillaCreada(servicio, plantillaId, tipo);
        }
    };

    if (!show) return null;

    return createPortal(
        <>
            <style>{MODAL_CSS}</style>
            <div
                className="modal fade show cups-rips-modal-portal"
                style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 20000 }}
                tabIndex="-1"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="modal-dialog modal-xl">
                    <div className="modal-content">
                        <div className="modal-header bg-info">
                            <h5 className="modal-title text-white">
                                <i className="fas fa-search mr-2" />
                                {title}
                            </h5>
                            <button type="button" className="close text-white" onClick={onClose}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            {clasificaciones.length > 0 && (
                                <div className="form-group mb-3">
                                    <label className="mb-1">
                                        <b>Clasificación</b>
                                    </label>
                                    <div className="chip-row">
                                        <button
                                            type="button"
                                            className={`chip-filter ${clasificacion === "" ? "active" : ""}`}
                                            onClick={() => setClasificacion("")}
                                        >
                                            Todas
                                        </button>
                                        {clasificaciones.map((clas) => (
                                            <button
                                                key={clas}
                                                type="button"
                                                className={`chip-filter ${clasificacion === clas ? "active" : ""}`}
                                                onClick={() => setClasificacion(clas)}
                                            >
                                                {clas}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="form-group">
                                <label>
                                    <b>Buscar Por Código o Nombre:</b>
                                </label>
                                <input
                                    ref={searchRef}
                                    type="text"
                                    className="form-control"
                                    placeholder="Busque por CUPS o nombre del servicio…"
                                    value={filtro}
                                    onChange={(e) => setFiltro(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                                <small className="form-text text-muted">
                                    Mostrando {filtrados.length} de {items.length} servicios
                                </small>
                            </div>

                            <div className="table-responsive" style={{ maxHeight: "55vh", overflowY: "auto" }}>
                                <table className="table table-hover table-sm mb-0">
                                    <thead className="thead-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                        <tr>
                                            <th style={{ width: "15%" }}>Código</th>
                                            <th style={{ width: "55%" }}>Nombre</th>
                                            <th style={{ width: "30%" }}>Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtrados.length > 0 ? (
                                            filtrados.map((item, index) => {
                                                const nombre = item.nombre != null ? String(item.nombre) : "";
                                                const conPlantilla = tienePlantilla(item, tipo, plantillasLocales);
                                                return (
                                                    <tr key={item.id != null ? `cups-${item.id}` : `cups-${index}`}>
                                                        <td>
                                                            <strong>{item.codigorips}</strong>
                                                        </td>
                                                        <td>
                                                            <div className="cups-rips-nombre-cell">
                                                                <span
                                                                    className="lista-precios-nombre-wrap"
                                                                    data-tip={nombre || undefined}
                                                                >
                                                                    <span className="lista-precios-nombre">
                                                                        {nombre || "—"}
                                                                    </span>
                                                                </span>
                                                                {conPlantilla && (
                                                                    <i
                                                                        className="fas fa-check-circle cups-rips-plantilla-check"
                                                                        title="Plantilla configurada"
                                                                    />
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cups-rips-actions">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-info"
                                                                    onClick={() => onSelect(item)}
                                                                >
                                                                    <i className="fas fa-check" /> Seleccionar
                                                                </button>
                                                                {!conPlantilla && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-crear-plantilla"
                                                                        onClick={() => abrirCrearPlantilla(item)}
                                                                        title="Parametrizar plantilla RIPS"
                                                                    >
                                                                        <i className="fas fa-plus-circle" /> Crear Plantilla
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="3" className="text-center text-muted">
                                                    {filtro || clasificacion
                                                        ? "No se encontraron resultados"
                                                        : "No hay servicios disponibles"}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>
                                <i className="fas fa-times" /> Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ModalPlantillaRipsRapida
                show={!!servicioPlantilla}
                onClose={() => setServicioPlantilla(null)}
                onSaved={onPlantillaGuardada}
                servicio={servicioPlantilla}
                tipo={tipo}
            />
        </>,
        document.body
    );
}

export default ModalCupsServicioRips;
export { tienePlantilla };
